import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { format, isAfter } from 'date-fns'

import {
  creditsPanelRequest,
  creditsPanelCreateMedicalRequest,
} from 'redux/modules/creditsPanel/requests'

import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { getTreatmentStatus } from 'services/treatments'
import { authChangePlan } from 'redux/actions'
import { INACTIVE } from 'settings/_personSettings'
import { PATIENT_SAVED_PLAN } from 'settings/_storageFlagsSettings'

const {
  CARE_PLUS,
  ENTERPRISE,
  CORPORATE_UNLIMITED,
  CORPORATE_LIMITED,
  CAPITATION
} = PlanGroupCategoryEnum

export const usePatientEligibility = (defaultPlan = false) => {
  const [corporateEligibility, setCorporateEligibility] = useState({})
  const [psychiatryEligibility, setPsychiatryEligibility] = useState({
    cycleStatus: undefined,
    cycleDate: undefined,
  })
  const [forceUpdate, setForceUpdate] = useState(0)
  const dispatch = useDispatch()
  const username = useSelector((state) => state?.auth?.userData?.username)
  const cardNumber = useSelector((state) =>
    state?.auth?.userData?.person_profile[0].plans.find((plan) => plan.default)
  )?.card_number
  const defaultPlanData = useSelector((state) => state?.auth?.profileSelected?.plans.find(pPlan => pPlan.default))
  const patientPlans = useSelector(
    (state) => state?.auth?.profileSelected?.plans?.filter(plan => plan.status_code !== INACTIVE)
  )
  const planGroupName = useSelector(
    (state) => state.auth.planSelected?.plan_group.name
  )
  const planCode = useSelector(
    (state) => state.auth.planSelected?.partner_plan_code
  )
  const planGroupCode = useSelector(
    (state) => state.auth.planSelected?.plan_group.code
  )
  const planGroupCategory = useSelector(
    (state) => state.auth.planSelected?.plan_group.category
  )
  const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(
    planGroupCategory
  )
  const isEnterprise = [ENTERPRISE, CARE_PLUS, CAPITATION].includes(planGroupCategory)
  const isCareplus = [CARE_PLUS].includes(planGroupCategory)
  const isCassi = planGroupName === 'CASSI'
  const isCapitation = planGroupCategory === CAPITATION

  const creditsPanel = useSelector((state) => state.creditsPanel)
  const creditsInfo = creditsPanel.data
  const creditsStatus = creditsInfo.status
  const reasonCode = creditsInfo?.reason_code
  const availableCredits = creditsInfo.available_credits
  const maxCredits = creditsInfo.max_appointment
  const enableDetails = creditsInfo.enable_details
  const validCreditsInfoDate = creditsInfo?.end_date ? new Date(creditsInfo.end_date) : new Date()
  const validDate = isCorporate ? corporateEligibility?.expirationDate : validCreditsInfoDate
  const expired = isAfter(new Date(), validDate)
  const errorButton = creditsPanel.errorButton
  const loadingButton = creditsPanel.loadingButton
  const creditsPanelErrorMessage = creditsPanel.error ? creditsPanel.messages : ''
  const solicitationDate = creditsInfo.date_created !== undefined 
    ? format(new Date(creditsInfo.date_created), 'dd/MM/yyyy')
    : ''

  const startDate = creditsInfo.start_date

  const isUnlimited = planGroupCategory === CORPORATE_UNLIMITED

  const [hasButton, setHasButton] = useState(false)
  const [loading, setLoading] = useState(true)
  const [
    psychiatristEligibilityLoading,
    setPsychiatristEligibilityLoading,
  ] = useState(true)

  useEffect(() => {
    setForceUpdate((prevState) => prevState + 1)
  }, [creditsInfo])

  useEffect(() => {
    if(creditsPanel.enableSolicitation) {
      setHasButton(creditsPanel.enableSolicitation)
    } else {
      setHasButton(false)
    }
  }, [creditsPanel])

  const handleRequestTreatementStatus = ({
    consultationType = 'PSIQ',
    planCode
  }) => {
    return new Promise((resolve, eject) => {
      getTreatmentStatus({
        userName: username,
        consultationType,
        planCode
      })
        .then(status => {
          setPsychiatryEligibility(status)
          setPsychiatristEligibilityLoading(false)
          resolve(status.cycleStatus)
        })
        .catch(() => {
          setPsychiatristEligibilityLoading(false)
          eject()
        })
    })
  }

  const handleUpdateSelectedPlan = (planCode) => {
    const newPlan = patientPlans?.find(pPlan => pPlan.partner_plan_code === planCode)

    if (newPlan) dispatch(authChangePlan(newPlan))
  }

  useEffect(() => {
    handleUpdateSelectedPlan(defaultPlan ? defaultPlanData?.partner_plan_code : localStorage.getItem(PATIENT_SAVED_PLAN))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[defaultPlan])

  useEffect(() => {
    if (username) handleRequestTreatementStatus({
      consultationType: 'PSIQ',
      planCode
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[username, planCode])

  useEffect(() => {
    setLoading(true)
    if (isCorporate) {
      getCorporateEligibility({
        username,
        planCode
      })
        .then(eligibility => {
          setCorporateEligibility(eligibility)
          setLoading(false)
        })
    } else if (isEnterprise) {
      dispatch(creditsPanelRequest())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCode])

  useEffect(() => {
    if (Object.keys(creditsInfo).length || creditsPanel.error) setLoading(false)
  }, [ creditsInfo, creditsPanel ])

  const onClickRequestCreditsButton = async () => {
    dispatch(creditsPanelCreateMedicalRequest(planCode, true))
    setHasButton(false)
  }

  return {
    isEnterprise,
    isCorporate,
    isCareplus,
    isCassi,
    isCapitation,
    hasButton: hasButton && isEnterprise,
    planGroupName,
    creditsStatus,
    availableCredits,
    maxCredits,
    enableDetails,
    validDate,
    expired,
    errorButton,
    loadingButton,
    solicitationDate,
    isUnlimited,
    onClickRequestCreditsButton,
    startDate,
    corporateEligibility,
    psychiatryEligibility,
    psychiatristEligibilityLoading,
    handleRequestTreatementStatus,
    loading,
    cardNumber,
    planGroupCode,
    onForceUpdate: () => dispatch(creditsPanelRequest()),
    handleUpdateSelectedPlan,
    forceUpdate,
    reasonCode,
    patientPlans,
    planCode,
    creditsPanelErrorMessage,
  }
}